import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, concatMap, debounceTime, map, of, share, shareReplay, skip, startWith, Subject, Subscription, switchMap, take } from "rxjs";
import { environment } from "../../environments/environment";
import { BaseService } from "./base.service";
import { FilterChip } from "../models/filter-chip";
import { TimeEntry, TimeSheet, TimesheetStatus } from "../models/time-entry";
import { TimeDateUtils } from "../time-tracker/date-utils";
import { catchError, EMPTY, Observable } from 'rxjs';
import { parseISO } from 'date-fns';
import { Project } from '../models/project';
import { mergeOfficialLeave } from './official-leave.service';

@Injectable({ providedIn: 'root' })
export class TimesheetService extends BaseService {
    private loadErrorSubject = new Subject<unknown>();
    loadError$ = this.loadErrorSubject.asObservable();
    private sortProperties: { [key: string]: string | string[] } = {
        "key": "Key", "title": "Title", "effortTitle": "Effort.Title", "oAccount": "OAccount", "mfk": "MFK", "customer": "Customer", "owner": "Owner", "date": "Date", "hours": "totalHours", "status": "Status",
        "displayName": 'DisplayName', "name-then-date": ['DisplayName', 'Date']
    };

    private timeSheetSubject = new Subject<TimeSheet>();
    timeSheet$ = this.timeSheetSubject.asObservable();

    private sourceUpdatedSubject = new BehaviorSubject<boolean>(false);
    sourceUpdated$ = this.sourceUpdatedSubject.asObservable();

    fetchedSubscription?: Subscription;
    fetchTimesheetsDeepSubscr?: Subscription;

    private saveQueueSubject = new Subject<[TimeSheet, Subject<TimeSheet>, boolean?]>();
    private saveQueue$ = this.saveQueueSubject.asObservable();

    constructor(private http: HttpClient) {
        super();
        this.saveQueue$.pipe(concatMap(([timesheet, subj, skipNotes]) => {
            const obs = this.http.put(environment.apiUrl + `timeentry?skipNotes=${skipNotes}`, timesheet).pipe(share());
            obs.subscribe({
                next: _ => {
                    subj.next(_);
                    this.sourceUpdatedSubject.next(true);
                },
                error: _ => {
                    subj.error(_);
                },
                complete: () => {
                    subj.complete();
                }
            });
            return obs;
        })).subscribe();
    }

    downloadCsv(filters: FilterChip[], pageSize: number = 10, pageIndex: number = 0, sortField: string | undefined = undefined, sortDirection: string | undefined = undefined) {
        const filterQuery = this.generatedFilterQuery(filters, pageSize, pageIndex, sortField, sortDirection);
        this.http.get(environment.apiUrl + "timeentry/search/export-csv?" + filterQuery, {
            responseType: 'blob', // Expect binary data
            headers: new HttpHeaders({
                'Accept': 'text/csv'
            })
        }).subscribe({
            next: blob => {
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = `submissions-export.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(downloadUrl);
            }, error: error => {
                console.error('CSV download failed:', error);
            }
        });
    }

    fetchTimesheet(weekStart: string, projects$?: Observable<Project[]>) {
        if (this.fetchedSubscription) this.fetchedSubscription.unsubscribe();

        this.fetchedSubscription = this.sourceUpdated$.pipe(
            switchMap(_ => {
                return this.http.get<TimeSheet>(environment.apiUrl + `timeentry/week/${weekStart}`).pipe(
                    // Week entries arrive without their projects, so legacy leave is identified from projects$.
                    switchMap(ts => (projects$ ?? of([])).pipe(
                        take(1),
                        map(projects => mergeOfficialLeave({ ...ts, weekStart }, projects)),
                    )),
                    catchError(error => { this.loadErrorSubject.next(error); return EMPTY; }),
                )
            }
            )
        ).subscribe(te => { this.timeSheetSubject.next(te); });
    }

    private generatedFilterQuery(allFilters: FilterChip[], pageSize: number = 10, pageIndex: number = 0, sortField: string | undefined = undefined, sortDirection: string | undefined = undefined) {
        const negFilters = allFilters.filter(f => f.isNegated);
        const filters = allFilters.filter(f => !f.isNegated);
        console.log(negFilters, filters);
        const keywords = filters.filter(c => c.chip == null).reduce((v, c) => v + ' ' + c.value, '');
        const propertyValuesSimple = filters.reduce((v, c) => { if (c.chip != null) v[c.chip] = c.value; return v; }, <{ [key: string]: string }>{});
        const propertyValuesNeg = negFilters.reduce((v, c) => { if (c.chip != null) v[c.chip] = c.value; return v; }, <{ [key: string]: string }>{});
        const filter = <any>{ keywords, 'propertyValues': propertyValuesSimple, 'not.propertyValues': propertyValuesNeg };
        if (pageSize) filter['pageSize'] = '' + pageSize
        if (pageIndex) filter['page'] = '' + pageIndex;
        if (sortField) {
            const sort = this.sortProperties[sortField];
            if (sort instanceof Array) {
                sort.forEach((s, i) => {
                    filter[`SortSpecifications[${i}].Property`] = s;
                    if (sortDirection) filter[`SortSpecifications[${i}].Direction`] = sortDirection == "asc" ? "Ascending" : "Descending";
                });
            } else {
                if (sortField) filter['SortSpecifications[0].Property'] = this.sortProperties[sortField];
                if (sortDirection) filter['SortSpecifications[0].Direction'] = sortDirection == "asc" ? "Ascending" : "Descending";
            }
        }
        return this.serialize(filter);
    }

    fetchTimesheetsDeep(filters: FilterChip[], pageSize: number = 10, pageIndex: number = 0, sortField: string | undefined = undefined, sortDirection: string | undefined = undefined) {
        if (this.fetchTimesheetsDeepSubscr) this.fetchTimesheetsDeepSubscr.unsubscribe();

        const subject = new BehaviorSubject("");
        const obs = subject.pipe(
            // The API attaches each owner's official leave summary, which is merged into the entries here.
            switchMap(filterQuery => this.http.get<TimeSheet[]>(environment.apiUrl + 'timeentry/search?' + filterQuery, { observe: 'response' })),
            map(response => {
                const paging = response.headers.get('Content-Range');
                const total = parseInt(paging?.split("/")[1] ?? '0');
                const pageSizeRecommendation = response.headers.get('X-Recommended-PageSize');
                console.log(pageSizeRecommendation);
                const recommendedPageSize = parseInt(pageSizeRecommendation ?? `${pageSize}`);
                const timeSheets = (response.body ?? []).map(ts => ({ ...mergeOfficialLeave(ts), ui: {} }));
                return { total, timeSheets, recommendedPageSize };
            })
        )
        this.fetchTimesheetsDeepSubscr = this.sourceUpdated$.subscribe(_ => {
            const filterQuery = this.generatedFilterQuery(filters, pageSize, pageIndex, sortField, sortDirection);
            subject.next(filterQuery);
        });
        return obs.pipe(debounceTime(50));
    }

    updateTimesheet(timeEntries: TimeEntry[], weekStart: string, submit = false, skipNotes: boolean = false) {
        const timesheet: TimeSheet = { weekStart, timeEntries };
        console.log(timesheet);
        if (submit)
            timesheet.status = TimesheetStatus.Submitted;

        // const obs = this.http.put(environment.apiUrl + `timeentry`, timesheet).pipe(share());
        // obs.subscribe(() => {
        //     this.sourceUpdatedSubject.next(true);
        // });
        // return obs;
        const subj = new Subject<Object>();
        this.saveQueueSubject.next([timesheet, subj, skipNotes]);
        return subj.asObservable();
    }

    setTimesheetStatus(ts: TimeSheet, notify: boolean = false) {
        const weekStart = TimeDateUtils.getDay(parseISO(ts.weekStart!));
        const ownerId = ts.owner?.id;
        const obs = this.http.put(environment.apiUrl + `timeentry/week/${weekStart}/status/${ownerId}?notify=${notify}`, ts).pipe(share());
        obs.subscribe(() => {
            this.sourceUpdatedSubject.next(true);
        });
        return obs;
    }
}
